import LoadingOverLay from "@/components/loading-overlay";
import { COOKIE_KEY } from "@/configs/global.config";
import { extractSubdomainFromToken } from "@/helpers/extractSubdomainFromToken";
import { refreshAccessToken } from "@/helpers/refreshAccessToken";
import Cookies from "js-cookie";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      let accessToken = Cookies.get(COOKIE_KEY.ACCESS_TOKEN);
      let refreshToken = Cookies.get(COOKIE_KEY.REFRESH_TOKEN);

      // If no tokens, redirect to login
      if (!accessToken && !refreshToken) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      // If no access token but has refresh token, try to refresh
      if (!accessToken && refreshToken) {
        try {
          const newTokens = await refreshAccessToken(refreshToken);
          if (newTokens) {
            accessToken = newTokens.access_token;
            refreshToken = newTokens.refresh_token.token;

            // Set cookies
            Cookies.set(COOKIE_KEY.ACCESS_TOKEN, accessToken!, { expires: 1 });
            Cookies.set(COOKIE_KEY.REFRESH_TOKEN, refreshToken!, {
              expires: new Date(newTokens.refresh_token.expires),
            });
          } else {
            // Clear cookies if refresh failed
            Cookies.remove(COOKIE_KEY.ACCESS_TOKEN);
            Cookies.remove(COOKIE_KEY.REFRESH_TOKEN);
            setIsAuthenticated(false);
            setIsLoading(false);
            return;
          }
        } catch (error) {
          Cookies.remove(COOKIE_KEY.ACCESS_TOKEN);
          Cookies.remove(COOKIE_KEY.REFRESH_TOKEN);
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }
      }

      // Check subdomain from token
      if (accessToken) {
        const subdomain = extractSubdomainFromToken(accessToken);
        if (subdomain !== "cms") {
          Cookies.remove(COOKIE_KEY.ACCESS_TOKEN);
          Cookies.remove(COOKIE_KEY.REFRESH_TOKEN);
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }
      }

      setIsAuthenticated(true);
      setIsLoading(false);
    };

    checkAuth();
  }, [location.pathname]);

  if (isLoading) {
    return <LoadingOverLay />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
